
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  addDoc, 
  getDocs, 
  onSnapshot,
  serverTimestamp,
  writeBatch,
  FirestoreError
} from "firebase/firestore";
import { db } from "./authService";
import { HistoryItem, AnalysisResult, CheckItem } from "../types";

const COLLECTION_NAME = 'history';

// Type guards for data validation
const isCheckItem = (item: unknown): item is CheckItem => {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  return (
    typeof obj.label === 'string' &&
    (obj.status === 'pass' || obj.status === 'fail') &&
    typeof obj.description === 'string'
  );
};

const isAnalysisResult = (data: unknown): data is AnalysisResult => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.score === 'number' &&
    Array.isArray(obj.checks) &&
    obj.checks.every(isCheckItem) &&
    typeof obj.optimizedPrompt === 'string' &&
    typeof obj.explanation === 'string'
  );
};

const isHistoryData = (data: unknown): data is { 
  timestamp?: { toMillis: () => number }; 
  originalPrompt: string; 
  analysisResult: AnalysisResult 
} => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.originalPrompt === 'string' &&
    isAnalysisResult(obj.analysisResult)
  );
};

/**
 * Safely transform Firestore document to HistoryItem
 * Returns null if data is invalid
 */
const safeTransformHistoryDoc = (
  docId: string, 
  data: unknown
): HistoryItem | null => {
  try {
    if (!isHistoryData(data)) {
      console.warn(`Invalid history data for doc ${docId}:`, data);
      return null;
    }
    
    return {
      id: docId,
      timestamp: data.timestamp?.toMillis() || Date.now(),
      originalPrompt: data.originalPrompt,
      analysisResult: data.analysisResult
    };
  } catch (e) {
    console.error(`Error transforming history doc ${docId}:`, e);
    return null;
  }
};

/**
 * Handle Firestore errors gracefully
 */
const handleFirestoreError = (error: unknown, operation: string): void => {
  if (error instanceof FirestoreError) {
    switch (error.code) {
      case 'permission-denied':
        console.error(`${operation}: Permission denied. User may need to re-authenticate.`);
        break;
      case 'unavailable':
        console.error(`${operation}: Firestore unavailable. Check network connection.`);
        break;
      case 'not-found':
        console.error(`${operation}: Document not found.`);
        break;
      case 'already-exists':
        console.error(`${operation}: Document already exists.`);
        break;
      case 'resource-exhausted':
        console.error(`${operation}: Quota exceeded.`);
        break;
      default:
        console.error(`${operation}: Firestore error (${error.code}):`, error.message);
    }
  } else {
    console.error(`${operation}: Unexpected error:`, error);
  }
};

export const subscribeToHistory = (
  userId: string, 
  callback: (items: HistoryItem[]) => void
): (() => void) => {
  try {
    const q = query(
      collection(db, `users/${userId}/${COLLECTION_NAME}`),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    return onSnapshot(
      q, 
      (snapshot) => {
        const items: HistoryItem[] = [];
        
        snapshot.docs.forEach(doc => {
          const item = safeTransformHistoryDoc(doc.id, doc.data());
          if (item) {
            items.push(item);
          }
        });
        
        callback(items);
      }, 
      (error) => {
        handleFirestoreError(error, 'subscribeToHistory');
        callback([]);
      }
    );
  } catch (e) {
    console.error("Error setting up history subscription:", e);
    // Return a no-op unsubscribe function
    return () => {};
  }
};

export const saveToHistory = async (
  userId: string, 
  originalPrompt: string, 
  result: AnalysisResult
): Promise<void> => {
  try {
    // Validate input before saving
    if (!originalPrompt || typeof originalPrompt !== 'string') {
      throw new Error('Invalid originalPrompt');
    }
    
    if (!isAnalysisResult(result)) {
      throw new Error('Invalid analysis result');
    }
    
    // Sanitize and limit data size
    const sanitizedResult: AnalysisResult = {
      score: Math.max(0, Math.min(100, result.score)),
      checks: result.checks.slice(0, 20).map(check => ({
        label: String(check.label).slice(0, 200),
        status: check.status === 'pass' ? 'pass' : 'fail',
        description: String(check.description).slice(0, 1000)
      })),
      optimizedPrompt: String(result.optimizedPrompt).slice(0, 10000),
      explanation: String(result.explanation).slice(0, 5000)
    };
    
    const colRef = collection(db, `users/${userId}/${COLLECTION_NAME}`);
    await addDoc(colRef, {
      timestamp: serverTimestamp(),
      originalPrompt: originalPrompt.slice(0, 10000),
      analysisResult: sanitizedResult
    });
  } catch (error) {
    handleFirestoreError(error, 'saveToHistory');
    throw error;
  }
};

export const clearHistory = async (userId: string): Promise<void> => {
  try {
    const colRef = collection(db, `users/${userId}/${COLLECTION_NAME}`);
    const snapshot = await getDocs(colRef);
    
    if (snapshot.empty) return;

    // Batch delete in chunks of 500 (Firestore limit)
    const BATCH_SIZE = 500;
    const docs = snapshot.docs;
    
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = docs.slice(i, i + BATCH_SIZE);
      
      chunk.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    }
  } catch (error) {
    handleFirestoreError(error, 'clearHistory');
    throw error;
  }
};

/**
 * Get history count for a user
 */
export const getHistoryCount = async (userId: string): Promise<number> => {
  try {
    const colRef = collection(db, `users/${userId}/${COLLECTION_NAME}`);
    const snapshot = await getDocs(colRef);
    return snapshot.size;
  } catch (error) {
    handleFirestoreError(error, 'getHistoryCount');
    return 0;
  }
};
